import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card, CardContent, CardMedia, CardActionArea,
  Typography, Box, Chip, Avatar,
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { BlogArticle } from '../../types/blog';

interface ArticleCardProps {
  article: BlogArticle;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article }) => {
  const excerpt = article.metaDescription || article.body.replace(/<[^>]+>/g, '').slice(0, 160) + '…';

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea component={RouterLink} to={`/blog/${article.slug}`} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {article.featuredImageUrl && (
          <CardMedia
            component="img"
            height="200"
            image={article.featuredImageUrl}
            alt={article.title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ flexGrow: 1 }}>
          <Chip label={article.category} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {article.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {excerpt}
          </Typography>

          {/* Author + meta */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={article.authorAvatar} alt={article.authorName} sx={{ width: 28, height: 28 }}>
                {article.authorName?.[0]}
              </Avatar>
              <Typography variant="caption" color="text.secondary">{article.authorName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {article.readingTimeMinutes} min read
              </Typography>
            </Box>
          </Box>

          {/* Date */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {new Date(article.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>

          {/* Tags */}
          {article.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {article.tags.slice(0, 3).map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              ))}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ArticleCard;
